/**
 * Access matrix — role-based, category × action × scope permissions.
 *
 * Each role grants, per data category, a VIEW and EDIT scope. Scopes are
 * hierarchical (all ⊇ reportingLine ⊇ own ⊇ none) and org-chart-driven:
 * "reportingLine" covers the viewer + their sub-tree (descendants via
 * managerEmail). Roles live in `config.accessRoles`; each user gets one
 * (`user.accessRole`). `custom` scope is deferred.
 *
 * v1 enforces VISIBILITY on the directory/public profile. Super/expense admins
 * bypass (this is a UI/visibility layer, like View-as-user — true server-side
 * field security is a later pass).
 */
export const ACCESS_CATEGORIES = [
    { key: 'publicProfile', label: 'Public profile' },
    { key: 'personalData', label: 'Personal data' },
    { key: 'employment', label: 'Employment' },
    { key: 'compensation', label: 'Compensation' },
    { key: 'bankDetails', label: 'Bank details' },
    { key: 'documents', label: 'Documents' },
    { key: 'absence', label: 'Absence' },
];
export const ACCESS_SCOPES = ['none', 'own', 'reportingLine', 'all'];
/** Layer assigned when a user has no access role (most junior — sees only
 *  public + their own private fields). */
export const LOWEST_ACCESS_LAYER = 99;
/** Default `minLayer` for a field set to "private" (Manager and above). */
export const DEFAULT_PRIVATE_MIN_LAYER = 4;
const P = (view, edit) => ({ view, edit });
/** Fill any missing categories with none/none so a grid is always complete. */
function grid(map) {
    const out = {};
    for (const c of ACCESS_CATEGORIES)
        out[c.key] = map[c.key] ?? P('none', 'none');
    return out;
}
const allCats = (perm) => grid(Object.fromEntries(ACCESS_CATEGORIES.map((c) => [c.key, perm])));
/** Seeded roles — used when `config.accessRoles` is absent; editable in the matrix. */
export const DEFAULT_ACCESS_ROLES = [
    { id: 'admin', name: 'Admin', layer: 1, scope: 'independent', permissions: allCats(P('all', 'all')) },
    { id: 'ceo', name: 'CEO/COO', layer: 2, scope: 'independent', permissions: allCats(P('all', 'none')) },
    {
        id: 'director',
        name: 'Director',
        layer: 3,
        scope: 'dependent',
        permissions: grid({
            publicProfile: P('all', 'own'),
            personalData: P('reportingLine', 'own'),
            employment: P('reportingLine', 'none'),
            compensation: P('reportingLine', 'none'),
            bankDetails: P('reportingLine', 'none'),
            documents: P('reportingLine', 'none'),
            absence: P('reportingLine', 'none'),
        }),
    },
    {
        id: 'manager',
        name: 'Manager',
        layer: 4,
        scope: 'dependent',
        permissions: grid({
            publicProfile: P('all', 'own'),
            personalData: P('reportingLine', 'own'),
            employment: P('reportingLine', 'none'),
            documents: P('reportingLine', 'none'),
            absence: P('reportingLine', 'none'),
        }),
    },
    {
        id: 'user',
        name: 'User',
        layer: 5,
        scope: 'independent',
        permissions: grid({
            publicProfile: P('all', 'own'),
            personalData: P('own', 'own'),
            employment: P('own', 'none'),
            compensation: P('own', 'none'),
            bankDetails: P('own', 'own'),
            documents: P('own', 'none'),
            absence: P('own', 'none'),
        }),
    },
];
export function resolveAccessRoles(config) {
    const r = config?.accessRoles;
    return r && r.length > 0 ? r : DEFAULT_ACCESS_ROLES;
}
export function findAccessRole(config, id) {
    const roles = resolveAccessRoles(config);
    return roles.find((r) => r.id === id) ?? roles.find((r) => r.id === 'user') ?? null;
}
/** Seniority layer for a user's access role (lower = more senior). Unknown /
 *  unassigned roles fall back to the most-junior layer. */
export function accessRoleLayer(config, id) {
    // Only fall back to the "user" role's layer when an id is actually assigned;
    // an unassigned user is the most junior of all.
    if (!id)
        return LOWEST_ACCESS_LAYER;
    const role = findAccessRole(config, id);
    return role?.layer ?? LOWEST_ACCESS_LAYER;
}
/** Hierarchy scope for a user's access role. Unknown/unassigned → 'independent'
 *  (the layer gate alone applies; matches pre-scope behavior). */
export function accessRoleScope(config, id) {
    if (!id)
        return 'independent';
    return findAccessRole(config, id)?.scope ?? 'independent';
}
/** Is `targetEmail` a descendant of `viewerEmail` in the org tree? */
function isDescendant(targetEmail, viewerEmail, byEmail) {
    let cur = byEmail.get(targetEmail.toLowerCase());
    const guard = new Set();
    while (cur?.managerEmail) {
        const m = cur.managerEmail.toLowerCase();
        if (m === viewerEmail.toLowerCase())
            return true;
        if (guard.has(m))
            break;
        guard.add(m);
        cur = byEmail.get(m);
    }
    return false;
}
/** Emails of everyone in `viewerEmail`'s reporting line (their org sub-tree).
 *  `includeSelf` prepends the viewer. Used for team/absence scoping. Returns
 *  lowercased emails. */
export function reportingLineEmails(viewerEmail, byEmail, includeSelf = true) {
    const viewer = viewerEmail.toLowerCase();
    const out = includeSelf ? [viewer] : [];
    for (const email of byEmail.keys()) {
        if (email === viewer)
            continue;
        if (isDescendant(email, viewerEmail, byEmail))
            out.push(email);
    }
    return out;
}
export function scopeCovers(scope, viewerEmail, targetEmail, byEmail) {
    if (scope === 'all')
        return true;
    if (scope === 'none')
        return false;
    if (viewerEmail.toLowerCase() === targetEmail.toLowerCase())
        return true; // own + reportingLine include self
    if (scope === 'reportingLine')
        return isDescendant(targetEmail, viewerEmail, byEmail);
    return false; // own does not cover others
}
/** Can `viewer` (with their accessRole) perform `action` on `category` of `targetEmail`? */
export function canAccess(config, viewer, action, category, targetEmail, byEmail) {
    const role = findAccessRole(config, viewer.accessRole);
    if (!role)
        return false;
    const scope = role.permissions?.[category]?.[action] ?? 'none';
    return scopeCovers(scope, viewer.email, targetEmail, byEmail);
}
/** Access-role ids whose holders get the read-only Expenses TEAM view
 *  (Michael, Sep 2026): Director + CEO/COO + Admin — managers deliberately
 *  NOT included. The role's `scope` decides reach: dependent = own reporting
 *  line, independent = org-wide. */
export const EXPENSE_TEAM_ACCESS_ROLES = ['director', 'ceo', 'admin'];
/** null = no team view; else the reach for this accessRole id. */
export function expenseTeamScope(accessRoleId, roles) {
    if (!accessRoleId || !EXPENSE_TEAM_ACCESS_ROLES.includes(accessRoleId))
        return null;
    const role = (roles && roles.length ? roles : DEFAULT_ACCESS_ROLES).find((r) => r.id === accessRoleId);
    return (role?.scope ?? 'independent') === 'dependent' ? 'reportingLine' : 'all';
}
//# sourceMappingURL=access.js.map